// Countries that store the street number in Address 2 and expect it rendered
// *before* the street name (Address) — e.g. "123 Main St". Every other country
// keeps the stored order (Address then Address 2).
// Mirrors constants.usCountryID / constants.caCountryID — inlined so this pure
// leaf util stays free of the heavy constants module (which boots MSAL on load).
const US_COUNTRY_ID = 'abc5d95c-129f-4837-988c-0bf4ae1f3b67';
const CA_COUNTRY_ID = '2fcbfdc4-9c25-4b58-874c-7c2625eaa750';
const streetNumberFirstCountryIDs: string[] = [US_COUNTRY_ID, CA_COUNTRY_ID];

export const isStreetNumberFirstCountry = (countryID?: string | null): boolean =>
    !!countryID && streetNumberFirstCountryIDs.includes(countryID);

/**
 * Returns the two street lines (Address / Address 2) ordered for display.
 * For US & Canada the street number (Address 2) comes before the street name
 * (Address); all other countries keep the stored order. Empty parts are removed
 * so callers can render or join whatever remains.
 */
export const orderStreetLines = (
    address?: string | null,
    address2?: string | null,
    countryID?: string | null
): string[] => {
    const streetName = (address ?? '').trim();
    const streetNumber = (address2 ?? '').trim();
    const ordered = isStreetNumberFirstCountry(countryID)
        ? [streetNumber, streetName]
        : [streetName, streetNumber];
    return ordered.filter(Boolean);
};

/** Street lines joined into a single space-separated segment. */
export const formatFacilityStreet = (
    address?: string | null,
    address2?: string | null,
    countryID?: string | null
): string => orderStreetLines(address, address2, countryID).join(' ');

/**
 * Street lines for a multi-line address block. US & Canada collapse the street
 * number (Address 2) and street name (Address) onto a single line — e.g.
 * "123 Main St". Every other country keeps Address and Address 2 on their own
 * separate lines, unchanged. Empty parts are removed.
 */
export const streetDisplayLines = (
    address?: string | null,
    address2?: string | null,
    countryID?: string | null
): string[] => {
    if (isStreetNumberFirstCountry(countryID)) {
        const combined = formatFacilityStreet(address, address2, countryID);
        return combined ? [combined] : [];
    }
    return orderStreetLines(address, address2, countryID);
};
